/**
 * src/data/seoIndustries.js
 *
 * Programmatic SEO data: 15 industries.
 * Each entry powers an /industries/:slug page rendered by IndustryPage.jsx.
 *
 * Image strategy:
 *   - Unsplash photos chosen by industry (commercially licensed, stable IDs)
 *   - URL: https://images.unsplash.com/photo-<id>?auto=format&fit=crop&w=1600&q=80
 */

const u = (id) =>
  `https://images.unsplash.com/photo-${id}?auto=format&fit=crop&w=1600&q=80`;

export const SEO_INDUSTRIES = [
  {
    slug: "healthcare",
    name: "Healthcare",
    plural: "Healthcare practices",
    hero: u("1576091160550-2173dba999ef"), // medical office
    hook: "Clarity matters more than cleverness when patients are deciding.",
    context:
      "Healthcare buyers don't need to be wooed — they need to be reassured. Our work in healthcare focuses on credibility architecture: how the website signals expertise, how reviews are surfaced, and how the booking flow reduces anxiety. Strategy first, design second.",
    painPoints: [
      "Outdated sites that don't match the quality of in-person care",
      "Generic content that fails HIPAA and trust-signal expectations",
      "Booking flows that lose patients between 'interested' and 'scheduled'",
      "No system to convert one-time patients into long-term relationships",
    ],
    caseStudy: {
      title: "Multi-location practice cut no-show rate 31% with a confirmation flow",
      problem:
        "Beautiful office, excellent reviews, 22% no-show rate burning $40k/mo. Confirmation system was a single SMS — easy to ignore.",
      fix: "Concierge Funnels redesigned the patient confirmation flow: pre-appointment value email, structured reminders, easy reschedule link.",
      result: "No-shows down to 15.2% in 60 days. Reclaimed $13k/mo in cancelled-revenue.",
    },
  },
  {
    slug: "real-estate",
    name: "Real Estate",
    plural: "Real estate teams + brokerages",
    hero: u("1564013799919-ab600027ffc6"), // modern luxury home
    hook: "Listings sell. Brands close.",
    context:
      "Real estate is the most over-saturated brand category in North America. Every agent looks the same, sounds the same, and converts at the same anemic rate. Our engagements here move agents from 'one of many' to 'the obvious referral' — through positioning, voice, and a follow-up system that doesn't ghost the lead.",
    painPoints: [
      "Brand reads identical to every other agent in the metro",
      "Hot leads that go cold within 3 days of a showing",
      "Newsletter graveyard — content that no one reads, sent inconsistently",
      "No system to capture seller-side leads from the buyer-side traffic",
    ],
    caseStudy: {
      title: "Luxury team 2.4x'd seller-side leads from buyer-side traffic",
      problem:
        "Strong buyer-side pipeline, weak listing pipeline. No mechanism to capture the seller-side intent of past buyers.",
      fix: "Strategy Call sequenced a 'home value report' lead magnet for past buyers. Built it in the Concierge Funnels engagement.",
      result: "Seller-side leads up 2.4x in 90 days. Two listings closed from the new flow in month 3.",
    },
  },
  {
    slug: "restaurants",
    name: "Restaurants",
    plural: "Restaurants + hospitality",
    hero: u("1517248135467-4c7edcad34c4"), // restaurant interior
    hook: "Cover counts compound when the brand has a clear point of view.",
    context:
      "Restaurants live or die on repeat traffic — which depends on whether customers can describe you in one sentence to a friend. We help operators sharpen the brand so the word-of-mouth machine has fuel, and build the local SEO and reservation flow so the curiosity converts.",
    painPoints: [
      "Hard-to-describe brand identity — staff can't pitch it consistently",
      "Strong weekend, weak weekday — no membership or local loyalty system",
      "Reservations leak — calls go unanswered, online flow is clunky",
      "Reviews are good but underused as marketing assets",
    ],
    caseStudy: {
      title: "Independent restaurant added $14k/mo from a membership program",
      problem:
        "Excellent food, packed weekends, ghost-town weekdays. Generic 'farm-to-table' messaging blurred the actual point of view.",
      fix: "Workshop sharpened the brand voice. Concierge Funnels built a tiered membership with weekday-exclusive perks.",
      result: "480 members in 5 months. Tuesday-Thursday revenue up 2.4x.",
    },
  },
  {
    slug: "professional-services",
    name: "Professional Services",
    plural: "Consultants + agencies + advisors",
    hero: u("1497366754035-f200968a6e72"), // office meeting
    hook: "Authority is earned by what you publish, not what you charge.",
    context:
      "Consultants, agencies, and advisors are sold on perceived authority. Our work moves these brands from 'one of many smart people' to 'the obvious phone call.' That usually starts with a methodology doc, a sharpened ICP, and a content architecture that produces compounding inbound.",
    painPoints: [
      "Founder-dependent sales — the business can't sell without the founder on the call",
      "Inbound is good on volume, bad on quality (price-shoppers, not ICP)",
      "Methodology is documented in someone's head, not in the brand",
      "Pricing feels like a negotiation every time instead of a published trade-off",
    ],
    caseStudy: {
      title: "Mid-market agency replaced founder-dependent sales in one quarter",
      problem:
        "Founder closed 100% of deals. New hires couldn't reproduce the conversion.",
      fix: "Brand Transformation Workshop output: methodology doc, ICP-specific pages, scripted sales-call playbook.",
      result: "Founder time in sales calls down 70%. Team-led deals closed at 84% of founder rate.",
    },
  },
  {
    slug: "ecommerce",
    name: "E-commerce",
    plural: "DTC + e-commerce brands",
    hero: u("1556742049-0cfed4f6a45d"), // packaging / boxes
    hook: "Conversion is a math problem with a brand answer.",
    context:
      "E-commerce founders are usually overrun by tactical fixes (a/b tests, app installs, ad creatives) and underserved by strategic clarity. Our work starts with a leak audit: which step of the funnel is actually under-performing, and why. Then we sequence the fix.",
    painPoints: [
      "Cart abandonment at 75%+ with no clear understanding of where",
      "Subscription offers that read as forced rather than valued",
      "Returns eating margin because PDP copy is vague",
      "Brand voice that doesn't survive a Meta ad",
    ],
    caseStudy: {
      title: "DTC apparel brand cut CAC 38% in 60 days",
      problem:
        "Spending $94 to acquire a $62 first-purchase customer. Beautiful Shopify site, no clarity on which step was leaking.",
      fix: "Marketing Audit identified shipping reveal as a 41% drop-off. Re-sequenced PDP, transparent shipping module, sharper subscription value prop.",
      result: "CAC to $58 in 60 days. Subscription conversion 2.3x.",
    },
  },
  {
    slug: "saas",
    name: "SaaS",
    plural: "SaaS + product-led companies",
    hero: u("1551434678-e076c223a692"), // laptop with code
    hook: "Engineering builds the product. Strategy ships the revenue.",
    context:
      "SaaS founders are often the worst storytellers of their own product. The page reads like a release note. Our work translates engineering-grade thinking into customer-grade language — without dumbing down the precision that makes the product real.",
    painPoints: [
      "Homepage hero reads like documentation, not a value prop",
      "Trial-to-paid stuck below 5% with no clarity on why",
      "Pricing page treated as an afterthought",
      "Onboarding emails written by the engineer who built the feature",
    ],
    caseStudy: {
      title: "Vertical SaaS doubled trial-to-paid in 90 days",
      problem:
        "Strong demos, 4.1% trial-to-paid. Onboarding emails were release notes; upgrade flow buried the value prop.",
      fix: "90-Day Revenue Regeneration: rebuilt the onboarding sequence, redesigned the upgrade page with a clear before/after, installed analytics.",
      result: "Trial-to-paid to 8.7%. MRR added in last 30 days > entire prior quarter.",
    },
  },
  {
    slug: "fitness",
    name: "Fitness & Wellness",
    plural: "Fitness + wellness studios",
    hero: u("1571902943202-507ec2618e8f"), // boutique gym
    hook: "Identity wins where commodity collapses.",
    context:
      "The boutique fitness market is one of the most copyable categories in North America. The studios that thrive aren't the ones with the best equipment — they're the ones with the clearest 'who this is for.' Our work locks the positioning in and builds the operational systems to defend it.",
    painPoints: [
      "Three competitor studios within two miles, all with similar messaging",
      "Strong founder reputation, weak studio brand independent of the founder",
      "Membership churn no one can explain",
      "Drop-in revenue stable, recurring revenue stuck",
    ],
    caseStudy: {
      title: "Boutique studio became the local default for working parents",
      problem:
        "Three studios within two miles competing on price. Generic 'community fitness' positioning.",
      fix: "Workshop reframed the studio for working parents — different class schedule, different on-site experience, different brand voice.",
      result: "Membership 2.6x in 6 months. Two competitor studios closed.",
    },
  },
  {
    slug: "beauty",
    name: "Beauty & Personal Care",
    plural: "Beauty + personal-care brands",
    hero: u("1522335789203-aaad2c5a87aa"), // beauty / skincare
    hero_alt: "Beauty + personal care",
    hook: "The brand IS the product in a category with infinite competition.",
    context:
      "Beauty is one of the most brand-leveraged categories on earth — and one of the most crowded. Our work moves brands past the 'pretty packaging' phase into operational depth: real positioning, real customer acquisition math, real retention systems.",
    painPoints: [
      "Looks great, sells fine, scales poorly",
      "CAC creeping up every quarter with no clear lever",
      "Subscription churn higher than category average",
      "PDP copy that doesn't survive a competitor comparison",
    ],
    caseStudy: {
      title: "Skincare brand cut acquisition cost 38% in 60 days",
      problem:
        "Spending $94 to acquire a $62 first-purchase customer. Beautiful site, vague subscription value prop.",
      fix: "Marketing Audit identified two leaks. Re-sequenced PDP, transparent shipping module, sharper subscription messaging.",
      result: "CAC $58. Subscription 2.3x.",
    },
  },
  {
    slug: "legal",
    name: "Legal",
    plural: "Law firms + legal-tech",
    hero: u("1589994965851-a8f479c573a9"), // gavel / books
    hook: "Trust signals out-convert flashy design every time.",
    context:
      "Legal buyers are risk-averse by default — they will not engage with a brand that signals 'this is a side hustle.' Our work in legal focuses on the credibility architecture of the website, the structure of the intake flow, and the messaging that signals 'serious professional, not flashy hustler.'",
    painPoints: [
      "Site looks like every other firm in the region",
      "Intake calls take 25 minutes each — no self-serve filter for fit",
      "Younger prospects ghost between call-to-book",
      "No way to demonstrate methodology without a sales call",
    ],
    caseStudy: {
      title: "Mid-size firm modernized intake without alienating long-term clients",
      problem:
        "98% phone intake. Younger prospects ghosting at the call-to-book step.",
      fix: "Hybrid intake: phone primary, self-serve form + calendar booking secondary. Strategy Call sequenced the launch.",
      result: "Self-serve at 40% of intake in 6 months. Long-term clients unaffected.",
    },
  },
  {
    slug: "home-services",
    name: "Home Services",
    plural: "Home services + skilled trades",
    hero: u("1518709268805-4e9042af2176"), // contractor tools
    hook: "Reviews are the new referral. The brand has to match.",
    context:
      "Home services brands win on reviews — and lose on weak websites that betray the reviews. Our work helps trades-led businesses present like the premium operators they already are, then builds the lead-flow architecture to convert the visibility into booked jobs.",
    painPoints: [
      "Excellent reviews, terrible website",
      "Quote requests pile up unanswered for 48+ hours",
      "Competitors with worse work win because they answer the phone faster",
      "No mechanism to capture and convert seasonal demand spikes",
    ],
    caseStudy: {
      title: "Home services brand became the local default in two seasons",
      problem:
        "Strong reviews, weak positioning. Competing on price against operators with worse work.",
      fix: "Workshop reframed positioning around a guarantee-led offer. New visual identity. Tighter lead-routing.",
      result: "Average ticket up 38%. Lead-to-booking rate doubled.",
    },
  },
  {
    slug: "financial-services",
    name: "Financial Services",
    plural: "Financial advisors + wealth + fintech",
    hero: u("1554224155-6726b3ff858f"), // bank / finance
    hook: "Authority needs to be visible without screaming.",
    context:
      "Financial buyers — whether retail or institutional — need to feel safe before they feel sold. Our work pairs strategic positioning with the visual restraint and content depth that signals 'this is a serious adult firm.' Compliance-aware, conversion-focused.",
    painPoints: [
      "Brand reads either 'too corporate' or 'too startup' for the actual ICP",
      "Lead magnets generate volume but no qualified prospects",
      "Content compliance review kills speed and consistency",
      "Advisor team can't represent the brand consistently across channels",
    ],
    caseStudy: {
      title: "Boutique wealth firm 3x'd qualified inbound in one quarter",
      problem:
        "Inbound strong on volume, terrible on quality. 90% of calls were sub-AUM threshold.",
      fix: "Workshop reframed positioning, voice, and lead-magnet stack. New ICP-specific landing page. Price-anchored audit offer screened upfront.",
      result: "Sub-threshold inquiries dropped to 12%. Pipeline 3.1x in 90 days.",
    },
  },
  {
    slug: "education",
    name: "Education",
    plural: "Schools + course creators + edtech",
    hero: u("1503676260728-1c00da094a0b"), // classroom / books
    hook: "Curriculum doesn't sell. Outcomes do.",
    context:
      "Education buyers — parents, students, professional learners — want to know what they'll be able to do at the end, not what they'll learn along the way. Our work translates curriculum into outcomes, and builds the funnel architecture that proves those outcomes.",
    painPoints: [
      "Marketing reads like a course catalog instead of a transformation",
      "Strong content, weak enrollment funnel",
      "Testimonials underused or absent from key decision pages",
      "No system to nurture browsers into applicants",
    ],
    caseStudy: {
      title: "Online course brand 5x'd enrollment with one positioning shift",
      problem:
        "Brand described the curriculum; ICP wanted to know if it would actually change their career.",
      fix: "Workshop output: every page reframed around the outcome, with testimonials integrated at every decision step.",
      result: "Enrollment 5.2x in 6 months. Reduced ad spend 22% along the way.",
    },
  },
  {
    slug: "non-profits",
    name: "Non-Profits",
    plural: "Charities + foundations + social enterprises",
    hero: u("1559027615-cd4628902d4a"), // community / volunteer
    hook: "Donors give to stories, not bullet points.",
    context:
      "Non-profits often have the strongest mission and the weakest brand discipline. Our work helps mission-led orgs operate like the institutions they want to be — clear positioning, professional brand systems, and donor journeys that convert intent into recurring support.",
    painPoints: [
      "Mission is strong; brand voice scatters across the site",
      "Donation page treated as a formality, not as a conversion surface",
      "No recurring-donor program despite a large one-time donor base",
      "Grant-side and donor-side messaging at war with each other",
    ],
    caseStudy: {
      title: "Mid-size non-profit grew recurring donors 3.6x in 9 months",
      problem:
        "Large one-time donor base. No recurring-donor program. Annual fundraising cycles felt like emergency rooms.",
      fix: "Concierge Funnels built a tiered recurring-donor flow with monthly outcome reports. Brand voice tightened to focus on impact stories.",
      result: "Recurring donors 3.6x. Annual fundraising stress reduced.",
    },
  },
  {
    slug: "automotive",
    name: "Automotive",
    plural: "Dealerships + repair shops + auto-tech",
    hero: u("1492144534655-ae79c964c9d7"), // car interior / dealership
    hook: "The buyer journey is half online before they call.",
    context:
      "Automotive buyers spend hours online before they ever pick up the phone — and most dealership and shop sites are still optimized for 2014 browsers. Our work modernizes the buyer journey so the digital experience matches the showroom experience.",
    painPoints: [
      "Inventory pages slow, poorly indexed, missing local SEO basics",
      "Leads from third-party listing sites go to spam folders",
      "Service-side revenue undervalued in the brand architecture",
      "No mechanism to convert past customers into service-revenue regulars",
    ],
    caseStudy: {
      title: "Multi-location dealer grew service revenue 2.1x with a service-side rebrand",
      problem:
        "Showroom drove most attention; service department under-marketed and under-revenue.",
      fix: "Brand Transformation Workshop separated service-side brand voice. Concierge Funnels built service-only landing pages and recurring-customer flow.",
      result: "Service revenue 2.1x in 12 months. Repeat-customer rate up 34%.",
    },
  },
  {
    slug: "construction",
    name: "Construction & Trades",
    plural: "Construction + skilled trades",
    hero: u("1503387762-592deb58ef4e"), // construction site
    hook: "Bids are won upstream of the quote, in the brand.",
    context:
      "Commercial and residential construction firms win on reputation, but the digital brand often lags 10 years behind the build quality. Our work brings the website into parity with the work, then builds the proposal-stage assets that close more bids without dropping price.",
    painPoints: [
      "Portfolio site doesn't reflect the actual quality of work",
      "Quote requests don't follow up consistently",
      "Past clients don't refer because the brand isn't memorable",
      "Pricing always feels like a negotiation rather than a confident published trade-off",
    ],
  },

  // ─── Wave 2 — additional industries ───────────────────────────────────
  {
    slug: "manufacturing",
    name: "Manufacturing",
    plural: "Manufacturers + industrial brands",
    hero: u("1565793298595-6a879b1d9492"),
    hook: "Specs sell to engineers. Story sells to procurement.",
    context:
      "Manufacturing brands typically have engineering-grade products and marketing-grade silence around them. Our work translates the spec sheet into the story buyers actually use to defend the purchase upstream — and rebuilds the digital surface to match the build quality.",
    painPoints: [
      "Website hasn't been updated since the last trade show booth",
      "Sales reps explain the product better than the homepage does",
      "RFP responses lean on PDFs because the website can't carry the load",
      "Distributor partners have better-looking sites than the manufacturer",
    ],
  },
  {
    slug: "insurance",
    name: "Insurance",
    plural: "Insurance + risk advisory",
    hero: u("1559526324-4b87b5e36e44"),
    hook: "Trust isn't a tagline. It's an architecture.",
    context:
      "Insurance buyers — personal lines, commercial, or specialty — buy from brands that signal stability and expertise without slipping into cliché. Our work modernizes the visual and copy systems while preserving the regulator-friendly conservatism the category requires.",
    painPoints: [
      "Brand reads like every other broker in the region",
      "Quote flows leak prospects between 'curious' and 'submit'",
      "Reviews are strong but invisible on key decision pages",
      "Newer products (cyber, specialty) struggle for shelf space against the legacy lines",
    ],
  },
  {
    slug: "logistics",
    name: "Logistics",
    plural: "Logistics + transportation",
    hero: u("1586528116311-ad8dd3c8310d"),
    hook: "Speed and reliability ARE the brand.",
    context:
      "Logistics brands compete on operational excellence — but the website often doesn't reflect that. Our work pairs strategy-led messaging with conversion infrastructure that lets shippers self-serve up to the contract stage.",
    painPoints: [
      "Site doesn't tell prospects what the brand actually moves",
      "Pricing pages are absent; everything routes to a sales call",
      "Tracking + service ops are world-class; the marketing implies otherwise",
      "Carrier partners and shippers see different sides of the brand",
    ],
  },
  {
    slug: "hospitality",
    name: "Hospitality",
    plural: "Hotels + hospitality brands",
    hero: u("1566073771259-6a8506099945"),
    hook: "The guest experience starts on the homepage.",
    context:
      "Hospitality brands are sold on emotion and booked on convenience. The brands that win pair atmospheric storytelling with a frictionless booking surface. Our work tightens both — strategy first, design second, booking flow last.",
    painPoints: [
      "Beautiful imagery, weak conversion",
      "Direct booking loses to OTA traffic on price + speed",
      "Property has personality; the brand doesn't communicate it",
      "Repeat-stay incentives aren't visible until check-out emails",
    ],
  },
  {
    slug: "agriculture",
    name: "Agriculture",
    plural: "Agriculture + ag-tech",
    hero: u("1500595046743-cd271d694d30"),
    hook: "Yield is the proof point. Story is the leverage.",
    context:
      "Agriculture buyers — producers, co-ops, ag-tech — value substance over polish, but tolerate substance-with-polish best of all. Our engagements lean into outcome-led messaging: yield, retention, operational lift — wrapped in a brand that reads contemporary without losing the heritage.",
    painPoints: [
      "Brand voice swings between 'too corporate' and 'too country'",
      "Tech-forward products struggle to land with traditional buyers",
      "Distributor partner sites carry better imagery than the manufacturer",
      "Trade-show collateral is sharper than the digital brand",
    ],
  },
  {
    slug: "pet-services",
    name: "Pet Services",
    plural: "Pet services + pet brands",
    hero: u("1583337130417-3346a1be7dee"),
    hook: "Pet-parents buy with their hearts and refer with their phones.",
    context:
      "Pet services live or die on word-of-mouth, and word-of-mouth lives on Instagram. Our work pairs warm, founder-led brand voice with the operational systems that convert tagged stories into booked appointments.",
    painPoints: [
      "Strong Instagram, weak booking flow",
      "Reviews are great but underused on key decision pages",
      "Pricing is hidden because 'every pet is different' — costs you the bookings",
      "No mechanism to convert one-time visits into recurring care plans",
    ],
  },
  {
    slug: "childcare",
    name: "Childcare",
    plural: "Childcare + early education",
    hero: u("1503454537195-1dcabb73ffb9"),
    hook: "Parents make the decision in 3 minutes — or never.",
    context:
      "Childcare buyers research thoroughly and decide quickly. Our work in this category tightens the credibility architecture — staff credentials, safety, philosophy, and tour-booking flow — so the brand can earn the visit in a single session.",
    painPoints: [
      "Centre's reputation is strong locally but invisible online",
      "Tour-booking flow is buried beneath general inquiry forms",
      "Staff bios aren't visible until parents call",
      "Waitlist comms feel chaotic instead of reassuring",
    ],
  },
  {
    slug: "senior-care",
    name: "Senior Care",
    plural: "Senior care + assisted living",
    hero: u("1573497019418-b400bb3ab074"),
    hook: "Adult children buy. Seniors live with the choice.",
    context:
      "Senior care decisions are nearly always made by adult children under emotional pressure. Our work clarifies the offer for that buyer, surfaces the trust signals they're looking for, and builds the tour-stage flow that turns research into a visit.",
    painPoints: [
      "Brand voice talks to seniors but adult children are the buyers",
      "Pricing transparency is treated as risk; it's a competitive moat",
      "Staff credentials buried; should be visible at decision points",
      "Tour bookings depend on receptionists answering the phone",
    ],
  },
  {
    slug: "veterinary",
    name: "Veterinary",
    plural: "Veterinary practices + clinics",
    hero: u("1583512603805-3cc6b41f3edb"),
    hook: "Reviews bring pet-parents in. The brand brings them back.",
    context:
      "Veterinary practices are some of the most word-of-mouth-driven businesses in healthcare. Our work strengthens the digital brand so referrals don't bounce on a tired website, and builds the recurring-care flow that lifts lifetime value.",
    painPoints: [
      "Excellent reviews, dated website",
      "Wellness plans are mentioned but never sold from the site",
      "Online booking exists but feels like a back door",
      "Practice staff are personalities; the brand doesn't surface them",
    ],
  },
  {
    slug: "religious-orgs",
    name: "Religious Organizations",
    plural: "Churches + religious orgs",
    hero: u("1438032005730-c779502df39b"),
    hook: "Welcoming should be a feature, not a leap of faith.",
    context:
      "Churches and religious organizations are mission-led but brand-shy. Our work treats the brand as ministry infrastructure: clear navigation for newcomers, honest representation of the community, and conversion flows that respect the journey from visitor to member.",
    painPoints: [
      "Newcomers can't find service times without scrolling 4 screens",
      "Ministry pages read like internal documents",
      "Giving page is treated as administrative, not pastoral",
      "Social media + website tell two different stories",
    ],
  },
];

export function getIndustry(slug) {
  return SEO_INDUSTRIES.find((i) => i.slug === slug) || null;
}
